import { StackNavigationProp, useNavigation } from '@bonanza-social/navigation';
import { Box, Icon, Image, Text, Timer } from '@bonanza-social/moonlight';
import { useTranslation } from '@bonanza-social/i18n';
import React, { memo, useCallback, useMemo } from 'react';
import { Pressable } from 'react-native';
import { selectStatus, useStatusPerks } from '@bonanza-social/status';
import { useSelector } from '@bonanza-social/toolkit/state';
import { BottomSheet } from '@bonanza-social/navigation';
import { selectSlots } from '../../state';
import { findEarliestTimestamp } from '@bonanza-social/toolkit/utils';
import styles from './ShopStatusPromotionBanner.module.css';

function ShopStatusPromotionBanner() {
  const [t] = useTranslation('shop');
  const status = useSelector(selectStatus);
  const { shopPromotionValue } = useStatusPerks();
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();

  const slots = useSelector(selectSlots);

  const allInCooldownTimestamp = useMemo(() => {
    const timestamps = slots.reduce((acc, item) => {
      if (item.cooldownEnd) {
        acc.push(item.cooldownEnd);
      }
      return acc;
    }, [] as string[]);

    // all items are in cooldown
    if (timestamps.length === slots.length) {
      return findEarliestTimestamp(timestamps);
    }
  }, [slots]);

  const onPress = useCallback(() => {
    if (status && status.playerStatus !== 'Member') {
      BottomSheet.open({
        namespace: 'shop',
        type: 'ShopStatusPromotions',
        borderGradient: `status${status.playerStatus}Background`,
        headerImageName: `status.${status.playerStatus}Gem`,
        borderStyle: 'allsides',
        onInfo: () => navigation.navigate('StatusInfo'),
      });
    }
  }, [status, navigation]);

  const { titleText, messageText } = useMemo(() => {
    if (allInCooldownTimestamp) {
      return {
        titleText: t('shopPromotionBannerCooldownTitle'),
        messageText: t('shopPromotionBannerCooldownMessage'),
      };
    }

    return {
      titleText: t('shopPromotionBannerActiveTitle', {
        discount: shopPromotionValue,
      }),
      messageText: t('shopPromotionBannerActiveMessage'),
    };
  }, [t, shopPromotionValue, allInCooldownTimestamp]);

  if (
    !shopPromotionValue ||
    !slots.length ||
    !status ||
    status.playerStatus === 'Member'
  ) {
    return null;
  }

  const { playerStatus } = status;

  return (
    <div className={styles.container}>
      <Pressable onPress={onPress}>
        <Box
          padding="space-300"
          borderRadius="radius-300"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box
            position="absolute"
            top="space-0"
            bottom="space-0"
            left="space-0"
            right="space-0"
            flexDirection="row"
          >
            <Box
              position="relative"
              variant="gradient"
              gradient={`status${playerStatus}Background`}
              width="50%"
              borderRadius="radius-300"
            >
              <Box
                variant="gradient"
                gradient="secondaryLeftOverlayBackground"
                position="absolute"
                top="space-0"
                bottom="space-0"
                left="space-0"
                right="space-0"
              />
            </Box>
            <Box
              backgroundColor="primaryBackgroundHover"
              width="50%"
              borderTopRightRadius="radius-300"
              borderBottomRightRadius="radius-300"
            />
          </Box>
          <Box flexDirection="row" gap="space-200">
            <Image
              marginLeft="-space-100"
              height="space-1000"
              aspectRatio={1}
              name={`status.${playerStatus}Gem`}
            />
            <Box gap="space-100">
              <Text variant="body-s-semi" color="darkSurfacePrimary">
                {titleText}
              </Text>
              <Box flexDirection="row" gap="space-100" alignItems="center">
                <Text variant="caption" color="darkSurfaceSecondary">
                  {messageText}
                </Text>
                {allInCooldownTimestamp ? (
                  <Box flexDirection="row" alignItems="center" gap="space-100">
                    <Image
                      name="common.DarkClock"
                      height="space-400"
                      aspectRatio={1}
                    />
                    <Timer
                      time={allInCooldownTimestamp}
                      variant="caption"
                      color="darkSurfacePrimary"
                      displayMode="precise"
                    />
                  </Box>
                ) : null}
              </Box>
            </Box>
          </Box>
          <Icon
            zIndex={10}
            name="arrowForwardIosOutlined"
            color="darkSurfacePrimary"
            width="space-400"
            height="space-400"
          />
        </Box>
      </Pressable>
    </div>
  );
}

export default memo(ShopStatusPromotionBanner);
